import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import TestCase from '../../models/customAssistant/TestCase';
import ModalLayout from '../structure/ModalLayout';
import ModalHeader from '../general/ModalHeader';
import FooterBar from '../structure/FooterBar';
import CustomScrollbars from '../structure/CustomScrollbars';
import Button from '../general/Button';
import TestCaseContainer from '../../hooks/TestCaseContainer';
import TextField from './TextField';
import { JsonEditor as Editor } from 'jsoneditor-react';
import {
    color_shades_dark, color_shades_darker, color_shades_darkest, color_shades_lighter,
    color_variants_green_2, color_variants_red_2, color_defaults_black
} from '../../constants/colors';
import useWindowSize from '../../hooks/UseWindowSize';
import Scrollbars from 'react-custom-scrollbars';
import set from 'lodash';
import ConversationSessionState from '../../models/sessionAttributes/ConversationSessionState';
import UserProfile from '../../models/userProfile/UserProfile';
import CheckboxField from './CheckboxField';

interface HistoryViewerProps {
    currentTestCase: TestCase;
}

interface Turn {
    user: string;
    assistant: string;
}

const TestCaseHistoryViewer: React.FC<HistoryViewerProps> = ({
    currentTestCase
}) => {

    const [turns, setTurns] = useState<Turn[]>([]);
    const testCaseContainer = TestCaseContainer.useContainer();
    const windowSize = useWindowSize();
    const MODAL_SCROLLBAR_SUBTRACTION: number = 185;
    const subScrollbarRef = useRef(null);
    useEffect(() => {
        if (subScrollbarRef?.current) {
            subScrollbarRef?.current.scrollToBottom();
        }
    }, [turns]);

    useEffect(() => {
        let tc = currentTestCase;
        var turnHistoryState = tc.sessionState.sessionAttributes["ConversationTurnHistoryState"];
        if (!turnHistoryState) {
            turnHistoryState = tc.sessionState.sessionAttributes["conversationTurnHistoryState"];
        }
        if (turnHistoryState?.TurnHistory?.length) {
            const newTurns = [];
            for (const turn of turnHistoryState.TurnHistory) {
                newTurns.push({
                    user: turn.UserInput,
                    assistant: turn.AssistantResponse
                });
            }
            setTurns(newTurns);
            if (subScrollbarRef?.current) {
                subScrollbarRef?.current.scrollToBottom();
            }
        }

    }, [currentTestCase])

    return (
        <HistoryViewer>
            <Scrollbars
                autoHide
                autoHeight
                autoHeightMin={250}
                autoHeightMax={250}
                ref={subScrollbarRef}
            >
                <HistoryViewerInScroll>
                    {
                        turns.map((turn, i) => (
                            <div key={i}>
                                <LabelWrapper>
                                    <SmallLabel>User</SmallLabel>
                                </LabelWrapper>
                                <LabelWrapper>
                                    <InfoLabel>{`${turn.user}`}</InfoLabel>
                                </LabelWrapper>
                                <LabelWrapper>
                                    <SmallLabel>Assistant</SmallLabel>
                                </LabelWrapper>
                                <LabelWrapper>
                                    <InfoLabel>{`${turn.assistant}`}</InfoLabel>
                                </LabelWrapper>
                            </div>
                        ))
                    }
                    {testCaseContainer?.fullResponses[currentTestCase?.name]?.outputSpeech?.length ?
                        <FinalTurnWrapper>
                            <LabelWrapper>
                                <BoldSmallLabel>User</BoldSmallLabel>
                            </LabelWrapper>
                            <LabelWrapper>
                                <BoldInfoLabel>{`${currentTestCase.text}`}</BoldInfoLabel>
                            </LabelWrapper>
                            <LabelWrapper>
                                <BoldSmallLabel>Assistant</BoldSmallLabel>
                            </LabelWrapper>
                            <LabelWrapper>
                                <BoldInfoLabel>{`${testCaseContainer?.fullResponses[currentTestCase?.name]?.outputSpeech}`}</BoldInfoLabel>
                            </LabelWrapper>
                        </FinalTurnWrapper>
                        :
                        currentTestCase?.text?.length ?
                            <FinalTurnWrapper>
                                <LabelWrapper>
                                    <BoldSmallLabel>User</BoldSmallLabel>
                                </LabelWrapper>
                                <LabelWrapper>
                                    <BoldInfoLabel>{`${currentTestCase.text}`}</BoldInfoLabel>
                                </LabelWrapper>
                            </FinalTurnWrapper>
                            :
                            <></>}
                </HistoryViewerInScroll>
            </Scrollbars>
        </HistoryViewer>)
}

export default TestCaseHistoryViewer;

interface BodyProps {
    minHeight?: number;
}

const LabelWrapper = styled.div`
    display: flex;
    flex-direction: row;
`;

const SmallLabel = styled.p`
font-weight: 400;
font-size: 14px;
color: ${color_shades_darkest};
margin: 0 0 4px 0px;
`;

const BoldSmallLabel = styled(SmallLabel)`
    font-weight: bold;
    color: ${color_defaults_black};
`;

const InfoLabel = styled.p`
font-weight: 300;
font-size: 14px;
font-style: italic;
color: ${color_shades_darker};
margin: 0 0 4px 0px;
`;

const BoldInfoLabel = styled(InfoLabel)`
    font-weight: bold;
    color: ${color_defaults_black};
`;

const FinalTurnWrapper = styled.div`
`;

const HistoryViewer = styled.div`
    /* visuals */
    border-radius: 8px;
    display: block;
    background-color: ${color_shades_lighter};
    height: 250px;
    margin: 0px 16px;
`;

const HistoryViewerInScroll = styled.div`
    padding: 16px 0px;
    margin: 0 16px;
`


